import React, { useEffect, useState } from "react";
import { Save, Loader2, AlertTriangle, Building2, CreditCard, MailIcon, MapPin, ShieldCheck, Send, ShieldAlert, RotateCcw, PowerOff, ShoppingCart, Video } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import api, { readError } from "@/lib/api";
import { invalidateSiteConfig } from "@/hooks/useSiteConfig";

const DEFAULT_HEADLINE = {
  hr: "Stranica u izradi",
  en: "Site under construction",
  de: "Webseite im Aufbau",
  fr: "Site en construction",
};
const DEFAULT_MESSAGE = {
  hr: "Naša nova web-stranica uskoro stiže. Za upite slobodno nas kontaktirajte.",
  en: "Our new website is launching soon. Get in touch in the meantime.",
  de: "Unsere neue Website wird in Kürze veröffentlicht. Bitte kontaktieren Sie uns in der Zwischenzeit.",
  fr: "Notre nouveau site sera bientôt en ligne. N'hésitez pas à nous contacter entre-temps.",
};

const SECTION_DEFS = {
  site_status: {
    label: "Site Status",
    icon: ShieldAlert,
    custom: true,
  },
  business: {
    label: "Business",
    icon: Building2,
    fields: [
      { key: "business_phone_landline", label: "Phone (landline, display)" },
      { key: "business_phone_landline_tel", label: "Phone (landline, dial)" },
      { key: "business_phone_mobile", label: "Phone (mobile, display)" },
      { key: "business_phone_mobile_tel", label: "Phone (mobile, dial)" },
      { key: "business_email", label: "Business email", type: "email" },
      { key: "business_address_line", label: "Address line" },
      { key: "business_address_city", label: "City" },
      { key: "business_address_postal", label: "Postal code" },
      { key: "business_address_country", label: "Country" },
    ],
  },
  payments: {
    label: "Payments",
    icon: CreditCard,
    fields: [
      { key: "stripe_publishable_key", label: "Stripe publishable key" },
      { key: "stripe_secret_key", label: "Stripe secret key", secret: true },
      { key: "stripe_webhook_secret", label: "Stripe webhook secret", secret: true },
      { key: "mypos_sid", label: "myPOS SID" },
      { key: "mypos_key_index", label: "myPOS key index" },
      { key: "mypos_private_key", label: "myPOS private key", secret: true, multiline: true },
    ],
  },
  smtp: {
    label: "SMTP",
    icon: MailIcon,
    fields: [
      { key: "smtp_host", label: "SMTP host" },
      { key: "smtp_port", label: "SMTP port" },
      { key: "smtp_user", label: "SMTP user" },
      { key: "smtp_password", label: "SMTP password", secret: true },
      { key: "smtp_from", label: "From address (display)", type: "email" },
    ],
  },
  maps: {
    label: "Analytics & Maps",
    icon: MapPin,
    fields: [
      { key: "ga4_id", label: "GA4 Measurement ID (e.g. G-XXXXXXX)" },
    ],
  },
  security: {
    label: "Security",
    icon: ShieldCheck,
    fields: [
      { key: "hcaptcha_site_key", label: "hCaptcha site key" },
      { key: "hcaptcha_secret_key", label: "hCaptcha secret key", secret: true },
    ],
  },
};

const MASK = "••••••••";

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data || {})).catch((err) => setError(readError(err, "Could not load settings.")));
  }, []);

  const setField = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setTouched((t) => ({ ...t, [k]: true }));
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = {};
      Object.entries(data || {}).forEach(([k, v]) => {
        // Skip masked placeholders and untouched secrets (backend ignores MASK anyway)
        if (v === MASK) return;
        payload[k] = v;
      });
      const { data: r } = await api.put("/admin/settings", payload);
      setData(r);
      setTouched({});
      invalidateSiteConfig();  // public site picks up changes immediately
      toast.success("Settings saved.");
    } catch (err) {
      setError(readError(err, "Could not save settings."));
    } finally {
      setBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const { data: r } = await api.post("/admin/settings/test-smtp", { locale: "hr" });
      setSmtpTestResult(r);
      if (r.status === "sent") toast.success(`Test email sent to ${r.to}`);
      else if (r.status === "skipped_no_smtp") toast.warning("SMTP not configured — fill the fields below and save first.");
      else toast.error(`SMTP test failed: ${r.error || "unknown error"}`);
    } catch (err) {
      toast.error(readError(err, "Could not run SMTP test."));
    } finally {
      setSmtpTesting(false);
    }
  };

  if (!data && !error) {
    return <div className="p-10 flex items-center justify-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="space-y-7 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">CMS</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">Settings</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Business details and integration credentials. Secret values are masked once saved.
          </p>
        </div>
        <Button data-testid="settings-save-btn" onClick={save} disabled={busy} className="shadow-interactive">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
          Save changes
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      <Tabs defaultValue="site_status" className="w-full">
        <TabsList className="bg-card border border-border/60 flex-wrap h-auto py-1.5" data-testid="settings-tabs">
          {Object.entries(SECTION_DEFS).map(([key, def]) => {
            const Icon = def.icon;
            return (
              <TabsTrigger key={key} value={key} data-testid={`tab-${key}`} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Icon className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                {def.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Custom Site Status tab */}
        <TabsContent value="site_status" className="mt-5">
          <SiteStatusPanel data={data} setData={setData} />
        </TabsContent>

        {Object.entries(SECTION_DEFS).filter(([k, def]) => !def.custom).map(([secKey, def]) => (
          <TabsContent key={secKey} value={secKey} className="mt-5">
            <Card className="bg-card border-border/60 shadow-ambient">
              <CardContent className="p-6 space-y-5">
                {def.fields.map((f) => {
                  const val = data?.[f.key] ?? "";
                  const showMaskHint = f.secret && val === MASK && !touched[f.key];
                  return (
                    <div key={f.key} className="space-y-1.5">
                      <Label className="text-xs">{f.label} {f.secret && <span className="text-muted-foreground">(secret)</span>}</Label>
                      {f.multiline ? (
                        <textarea
                          value={val}
                          onChange={(e) => setField(f.key, e.target.value)}
                          rows={5}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid={`field-${f.key}`}
                        />
                      ) : (
                        <Input
                          type={f.type || "text"}
                          value={val}
                          onChange={(e) => setField(f.key, e.target.value)}
                          className="h-10 bg-background font-mono text-sm"
                          data-testid={`field-${f.key}`}
                        />
                      )}
                      {showMaskHint && (
                        <p className="text-xs text-muted-foreground">A value is saved (hidden). Type to replace it; leave as <code>{MASK}</code> to keep.</p>
                      )}
                    </div>
                  );
                })}
                {secKey === "smtp" && (
                  <div className="pt-3 border-t border-border/60 space-y-2">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-xs font-medium text-foreground flex items-center gap-2"><MailIcon className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} /> Send Test Email</div>
                        <p className="text-xs text-muted-foreground mt-0.5">Sends a brand-styled test email to your admin address using the credentials above. Save before testing.</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={sendTestEmail}
                        disabled={smtpTesting}
                        data-testid="smtp-test-btn"
                        className="shrink-0"
                      >
                        {smtpTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
                        Send test
                      </Button>
                    </div>
                    {smtpTestResult && (
                      <div
                        data-testid="smtp-test-result"
                        className={`text-xs rounded-md px-3 py-2 ${
                          smtpTestResult.status === "sent"
                            ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
                            : smtpTestResult.status === "skipped_no_smtp"
                              ? "bg-amber-50 text-amber-900 border border-amber-200"
                              : "bg-rose-50 text-rose-900 border border-rose-200"
                        }`}
                      >
                        {smtpTestResult.status === "sent" && <>Sent to <strong>{smtpTestResult.to}</strong>. Check your inbox.</>}
                        {smtpTestResult.status === "skipped_no_smtp" && <>SMTP host is empty. Fill in the fields above, save, then try again.</>}
                        {smtpTestResult.status === "failed" && <>Failed: <code className="font-mono">{smtpTestResult.error}</code></>}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// ===== Site Status panel =====
function SiteStatusPanel({ data, setData }) {
  const [lastToggle, setLastToggle] = useState(null);
  const [lastCommerceToggle, setLastCommerceToggle] = useState(null);
  const enabled = !!data?.maintenance_mode_enabled;
  const commerceOn = !!data?.commerce_mode_enabled;
  const heroVideo = data?.hero_video_url || "";
  const headlines = data?.maintenance_headline || {};
  const messages = data?.maintenance_message || {};

  useEffect(() => {
    api
      .get("/admin/audit-logs", { params: { action: enabled ? "MAINTENANCE_MODE_ENABLED" : "MAINTENANCE_MODE_DISABLED", limit: 1 } })
      .then((r) => setLastToggle((r.data?.items || [])[0] || null))
      .catch(() => {});
  }, [enabled]);

  useEffect(() => {
    api
      .get("/admin/audit-logs", { params: { action: commerceOn ? "COMMERCE_MODE_ENABLED" : "COMMERCE_MODE_DISABLED", limit: 1 } })
      .then((r) => setLastCommerceToggle((r.data?.items || [])[0] || null))
      .catch(() => {});
  }, [commerceOn]);

  const toggle = (v) => setData((d) => ({ ...d, maintenance_mode_enabled: v }));
  const toggleCommerce = (v) => setData((d) => ({ ...d, commerce_mode_enabled: v }));
  const setHeroVideo = (v) => setData((d) => ({ ...d, hero_video_url: v }));
  const setHeadline = (loc, v) => setData((d) => ({ ...d, maintenance_headline: { ...(d?.maintenance_headline || {}), [loc]: v } }));
  const setMessage = (loc, v) => setData((d) => ({ ...d, maintenance_message: { ...(d?.maintenance_message || {}), [loc]: v } }));
  const restoreDefaults = () => setData((d) => ({ ...d, maintenance_headline: { ...DEFAULT_HEADLINE }, maintenance_message: { ...DEFAULT_MESSAGE } }));

  return (
    <div className="space-y-6">
    <Card className={`border-border/60 shadow-ambient ${enabled ? "bg-amber-50/60" : "bg-card"}`} data-testid="site-status-panel">
      <CardContent className="p-6 space-y-7">
        {/* Toggle row */}
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div className="space-y-2 min-w-0 max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary flex items-center gap-1.5">
              {enabled ? <ShieldAlert className="h-3 w-3" strokeWidth={2} /> : <PowerOff className="h-3 w-3" strokeWidth={2} />}
              {enabled ? "Maintenance mode active" : "Site is live"}
            </div>
            <h2 className="font-serif text-2xl text-foreground">Maintenance mode</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              When ON, anonymous visitors see an under-construction page on every public URL. You can still browse the live site normally while signed in. Admin pages, sitemap.xml, and in-progress checkouts (success/cancel) stay accessible.
            </p>
            {lastToggle && (
              <div className="text-xs text-muted-foreground flex items-center flex-wrap gap-1">
                <span>Last toggled</span>
                <Badge variant="outline" className="font-mono text-[10px]">{lastToggle.action}</Badge>
                <span>by</span>
                <span className="text-foreground">{lastToggle.actor_email || "system"}</span>
                <span>· {(lastToggle.created_at || "").slice(0, 19).replace("T", " ")}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0 rounded-xl border border-border/60 bg-background px-4 py-3" data-testid="maintenance-toggle-row">
            <span className={`text-xs uppercase tracking-wider ${enabled ? "text-muted-foreground" : "text-foreground"}`}>Off</span>
            <Switch
              checked={enabled}
              onCheckedChange={toggle}
              data-testid="maintenance-toggle"
              className="data-[state=checked]:bg-primary"
            />
            <span className={`text-xs uppercase tracking-wider ${enabled ? "text-primary font-semibold" : "text-muted-foreground"}`}>On</span>
          </div>
        </div>

        {/* Locale editors */}
        <div className="space-y-4 pt-2 border-t border-border/60">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs font-medium text-foreground">Cover page copy</div>
              <p className="text-xs text-muted-foreground mt-0.5">Visible to anonymous visitors while maintenance is ON. Localised for each language.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={restoreDefaults} data-testid="maintenance-restore-defaults">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Restore defaults
            </Button>
          </div>
          {["hr", "en", "de", "fr"].map((loc) => (
            <div key={loc} className="rounded-lg border border-border/60 bg-background p-4 space-y-3" data-testid={`maintenance-locale-${loc}`}>
              <div className="flex items-baseline justify-between">
                <Badge variant="outline" className="font-mono text-[10px] uppercase">{loc}</Badge>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {loc === "hr" ? "Croatian" : loc === "en" ? "English" : loc === "de" ? "German" : "French"}
                </span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Headline</Label>
                <Input
                  value={headlines[loc] ?? ""}
                  onChange={(e) => setHeadline(loc, e.target.value)}
                  placeholder={DEFAULT_HEADLINE[loc]}
                  className="h-10"
                  data-testid={`maintenance-headline-${loc}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Message</Label>
                <textarea
                  value={messages[loc] ?? ""}
                  onChange={(e) => setMessage(loc, e.target.value)}
                  rows={3}
                  placeholder={DEFAULT_MESSAGE[loc]}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  data-testid={`maintenance-message-${loc}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50/70 text-amber-900 text-xs p-3 leading-relaxed">
          <strong className="font-semibold">Heads up:</strong> changes take effect immediately for you, and within ~60 seconds for everyone else (matches the public site-config cache). Click <strong>Save changes</strong> at the top right to commit.
        </div>
      </CardContent>
    </Card>

    {/* ===== Commerce toggle ===== */}
    <Card className="border-border/60 shadow-ambient bg-card" data-testid="commerce-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div className="space-y-2 min-w-0 max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary flex items-center gap-1.5">
              <ShoppingCart className="h-3 w-3" strokeWidth={2} />
              {commerceOn ? "E-commerce enabled" : "Contact-first (commerce disabled)"}
            </div>
            <h2 className="font-serif text-2xl text-foreground">E-commerce</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              E-trgovina omogućuje košaricu, konfigurator i online plaćanje. Kada je isključena, posjetitelji šalju upite putem obrasca.
              <span className="block mt-1 text-muted-foreground/80">When off, cart / configurator / checkout routes are hidden and each instrument sub-page shows an inquiry form instead.</span>
            </p>
            {lastCommerceToggle && (
              <div className="text-xs text-muted-foreground flex items-center flex-wrap gap-1">
                <span>Last toggled</span>
                <Badge variant="outline" className="font-mono text-[10px]">{lastCommerceToggle.action}</Badge>
                <span>by</span>
                <span className="text-foreground">{lastCommerceToggle.actor_email || "system"}</span>
                <span>· {(lastCommerceToggle.created_at || "").slice(0, 19).replace("T", " ")}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0 rounded-xl border border-border/60 bg-background px-4 py-3" data-testid="commerce-toggle-row">
            <span className={`text-xs uppercase tracking-wider ${commerceOn ? "text-muted-foreground" : "text-foreground"}`}>Off</span>
            <Switch
              checked={commerceOn}
              onCheckedChange={toggleCommerce}
              data-testid="commerce-toggle"
              className="data-[state=checked]:bg-primary"
            />
            <span className={`text-xs uppercase tracking-wider ${commerceOn ? "text-primary font-semibold" : "text-muted-foreground"}`}>On</span>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* ===== Homepage hero video URL ===== */}
    <Card className="border-border/60 shadow-ambient bg-card" data-testid="hero-video-panel">
      <CardContent className="p-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-primary flex items-center gap-1.5">
            <Video className="h-3 w-3" strokeWidth={2} />
            Optional
          </div>
          <h2 className="font-serif text-2xl text-foreground">Homepage hero video</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mt-1">
            If set, plays a looping 4-second video in the hero slot on <code className="text-xs bg-muted px-1 py-0.5 rounded">/</code>. When empty, the still workshop photo with subtle Ken Burns motion is used instead.<br />
            <span className="text-xs text-muted-foreground/85">Recommended: MP4, ≤ 3 MB, muted, 16:9 or wider, generated to loop seamlessly. You can host on any CDN or upload via <strong>/admin/gallery</strong> and paste the resulting URL here.</span>
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Video URL</Label>
            <Input
              type="url"
              value={heroVideo}
              onChange={(e) => setHeroVideo(e.target.value)}
              placeholder="https://example.com/hero.mp4"
              className="h-10 font-mono text-xs"
              data-testid="hero-video-url-input"
            />
          </div>
          {heroVideo && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setHeroVideo("")}
              data-testid="hero-video-clear"
              className="shrink-0"
            >
              Clear
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
    </div>
  );
}
